import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  flexRender, getCoreRowModel, getFilteredRowModel,
  getPaginationRowModel, getSortedRowModel, useReactTable,
} from '@tanstack/react-table';
import { Download, Search, ArrowUpDown, AlertCircle, FileSpreadsheet, Loader2, ArrowLeft, Mail, Send, Sparkles, FileArchive, LockKeyhole } from 'lucide-react';
import * as XLSX from 'xlsx';
import { Alert, AlertDescription } from '../components/ui/alert';
import IFCValidationPanel from '../components/IFCValidationPanel';
import IFCCompositionPanel from '../components/IFCCompositionPanel';
import { ValidationKpiDashboard, SchemaBadge, AuthoringToolBadge, ModelBadge } from '../components/ValidationKpiDashboard';
import { RulesNotRunPanel } from '../components/RulesNotRunPanel';
import { CategoryBreakdown } from '../components/CategoryBreakdown';
import { PropertyCoveragePanel } from '../components/PropertyCoveragePanel';
import { CategoryFilterBanner } from '../components/CategoryFilterBanner';
import { useCategoryFilter } from '../lib/useCategoryFilter';
import {
  applicableElementChecks,
  resolveActiveRuleEvidence,
  resolveElementStatusCounts,
} from '../lib/auditCompliance';
import { saveBlobWithPicker, openManualDownload } from '../lib/fileSave';
import { complianceReportDownloadName } from '../lib/reportFilenames';
import { toast } from 'sonner';
import { apiFetch } from '../lib/supabase';
import IdsValidationResultsModal from '../components/IdsValidationResultsModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// QG (Quality Gate) baseline checks — the checks that drive an element's
// confidence_score / certification_status. Distinct from custom_bep_rules,
// which never affects confidence. Mirrors services/rule_engine.py:
// run_quality_gate's profile-gated checks.
const QG_CATEGORIES = [
  'naming_compliance', 'ifc_schema', 'fm_parameters',
  'geometry_completeness', 'spatial_containment',
];

const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
const formatPercent = (value) => (isFiniteNumber(value) ? `${value.toFixed(2)}%` : 'Unavailable');

function summaryMetric(metric, passKey = 'pass', failKey = 'fail') {
  if (!metric || !isFiniteNumber(metric.assessed)) return { available: false };
  if (metric.assessed === 0) {
    return { available: true, assessed: 0, skipped: metric.skipped, state: isFiniteNumber(metric.skipped) && metric.skipped > 0 ? 'N/A' : 'Not assessed' };
  }
  const pass = metric[passKey];
  const fail = metric[failKey];
  const rate = isFiniteNumber(metric.pass_rate)
    ? metric.pass_rate
    : (isFiniteNumber(pass) && isFiniteNumber(fail) ? (pass / (pass + fail)) * 100 : null);
  return { available: isFiniteNumber(rate), assessed: metric.assessed, pass, fail, skipped: metric.skipped, rate };
}

export default function ResultsPage() {
  const { auditId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [audit, setAudit] = useState(null);
  const isReadOnly = Boolean(audit?.is_read_only);
  const [loading, setLoading] = useState(true);
  // Real DB row count from the backend's pagination metadata. The AIM
  // register is fetched page-by-page (PAGE_SIZE rows per request) until
  // ``pagination.has_more`` is false — never capped at 100/1000/anything.
  const [totalRecords, setTotalRecords] = useState(0);
  // True while background pages are still streaming in. Charts render
  // progressively; exports stay locked until the dataset is complete.
  const [fetchingAll, setFetchingAll] = useState(false);
  // Backend pre-computed chart aggregates (full-dataset, few-KB
  // payload). Charts render from this in <1s regardless of model
  // size; the streamed rows remain the source for table + exports.
  const [aggregates, setAggregates] = useState(null);
  const [error, setError] = useState('');
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [remediationLoading, setRemediationLoading] = useState(false);
  // Per-category PDF — tracks which category row (if any) is currently
  // generating. Allows the row's button to show a spinner without
  // blocking other rows from being clicked.
  const [categoryReportLoadingFor, setCategoryReportLoadingFor] = useState(null);
  const [bcfLoading, setBcfLoading] = useState(false);

  // IfcTester validate-ids results modal state (trigger button removed).
  const [idsValidationResult, setIdsValidationResult] = useState(null);
  const [idsValidationModalOpen, setIdsValidationModalOpen] = useState(false);

  // Interactive AIM dashboard: clicking a row in "Results by Validation
  // Category" filters every chart above to that category. null = unfiltered
  // (default behaviour).
  //
  // Caching strategy: we keep ONE map from category-key → fetched rule
  // rows. The previous version pulled the whole `validation_results` set
  // up-front and paged the entire thing client-side — that worked for the
  // 700-row VOC_EARLY demo but fell apart on real audits (43k+ and 1M+
  // rule rows seen in this DB). Now we fetch only the selected
  // category's rows, which is at most a few hundred rows on every audit
  // we've shipped. Each category is cached for the lifetime of the page
  // view, so toggling is instant after the first click.
  // Category filter state is encapsulated in ``useCategoryFilter``
  // (Feb 26, 2026 refactor — see lib/useCategoryFilter.js). The hook
  // owns:
  //   • selectedCategory + setter + clear helper
  //   • lazy paginated fetch of the selected category's validation_results
  //   • per-category cache so toggling is instant after first click
  //   • derived chartData / chartSummary that fall through to baseData
  //     when no filter is active.
  // The hook is instantiated LATER in this component, after baseData and
  // ``audit`` are in scope. Look for the ``useCategoryFilter({...})``
  // call below.

  // Generic blob-download helper for the two AI/Standards artefacts we
  // stream from the backend. apiFetch attaches the Supabase JWT. When
  // the browser supports the File System Access API (Chrome/Edge/
  // Brave) the user gets a native "Save as…" dialog and picks the
  // destination — otherwise the file falls through to the Downloads
  // folder via the legacy anchor-download path. Toast feedback fires
  // on success, cancel, and failure so the click can never look silent.
  const _downloadBlob = async (url, fallbackName, errPrefix, mimeType) => {
    const tLoad = toast.loading(`Preparing ${errPrefix.toLowerCase()}…`);
    try {
      const resp = await apiFetch(url, {
        headers: { Accept: 'application/octet-stream' },
      });
      if (!resp.ok) {
        const t = await resp.text().catch(() => '');
        throw new Error(`${errPrefix} (${resp.status}): ${t.slice(0, 200)}`);
      }
      const blob = await resp.blob();
      const cd = resp.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : fallbackName;
      const outcome = await saveBlobWithPicker(blob, filename, mimeType);
      toast.dismiss(tLoad);
      if (outcome.cancelled) return;
      if (outcome.manual_action) {
        // Iframe context — show a sticky toast with a button. THE USER
        // clicking the button is what opens the helper tab; doing it
        // programmatically from JS is silently blocked by the iframe
        // sandbox. The helper tab then calls ``showSaveFilePicker``
        // from its top-level context, which DOES work — the user
        // gets a real native Save As dialog and can pick any folder.
        const sizeKb = `${(blob.size / 1024).toFixed(1)} KB`;
        toast.message(`${filename} is ready`, {
          description: `${sizeKb} · Click "Save report" to pick where to save it on your computer.`,
          duration: 30000,
          action: {
            label: 'Save report',
            onClick: () => {
              const opened = openManualDownload(outcome, (progress) => {
                if (progress.type === 'aha-save-complete') {
                  toast.success(`Saved · ${progress.filename}`, { duration: 5000 });
                } else if (progress.type === 'aha-save-cancelled') {
                  toast.message('Save cancelled', { duration: 4000 });
                } else if (progress.type === 'aha-save-error') {
                  toast.error('Could not save the file', {
                    description: progress.error || 'Unknown error',
                    duration: 10000,
                  });
                } else if (progress.type === 'popup-blocked') {
                  toast.error('Pop-up was blocked', {
                    description:
                      'Allow pop-ups for this site so AHA can open the Save dialog. The file opened in a preview tab as a fallback — use Ctrl/Cmd+S there.',
                    duration: 12000,
                  });
                }
              });
              if (!opened) {
                toast.error('Could not open the Save dialog', {
                  description:
                    'Allow pop-ups for this site, or open AHA in its own browser tab (outside the preview).',
                  duration: 12000,
                });
              }
            },
          },
        });
        return;
      }
      const successMsg =
        outcome.location === 'picked'
          ? `Saved to your chosen location · ${outcome.filename}`
          : `Saved to your Downloads folder · ${outcome.filename}`;
      toast.success(successMsg, {
        description: `${(blob.size / 1024).toFixed(1)} KB`,
        duration: 6000,
      });
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error(errPrefix, { description: e.message || 'Unknown error', duration: 8000 });
      throw e;
    }
  };

  /**
   * Trigger an AI remediation report via the async start → poll → download flow.
   *
   * Why async (Feb 26, 2026):
   *   The previous synchronous endpoint kept the browser HTTP request
   *   alive for the whole OpenAI call. When OpenAI was congested
   *   (503 → retries → 120 s) Kubernetes ingress timed out at ~60 s
   *   and returned 502 — even when the backend eventually got the PDF.
   *
   *   Now we:
   *     1. POST /remediation-report/start → get back {job_id}.
   *     2. Poll GET /api/ai-jobs/{job_id} every 2 s until status moves
   *        to 'complete' or 'failed'. Each poll is <200 ms — no ingress
   *        timeout ever fires.
   *     3. GET /api/ai-jobs/{job_id}/download → stream the cached PDF
   *        and hand it to the same saveBlobWithPicker helper as before.
   *
   *   ``remediationLoading`` stays true through the whole poll loop so
   *   the button shows "Generating…" continuously.
   */
  const downloadRemediationReport = async () => {
    if (!auditId) return;
    setRemediationLoading(true);
    const tLoad = toast.loading('Starting BIM Compliance & Advisory Report…');
    try {
      // 1) Kick off background generation.
      const startResp = await apiFetch(
        `${API_URL}/api/audits/${auditId}/remediation-report/start`,
        { method: 'POST' },
      );
      if (!startResp.ok) {
        const body = await startResp.text();
        throw new Error(`Start failed (${startResp.status}): ${body.slice(0, 200)}`);
      }
      const { job_id } = await startResp.json();

      // 2) Poll the status endpoint until terminal. The 3-minute cap is
      // generous — typical generation is 15-60 s; we give OpenAI plenty
      // of room for retries on congested days.
      toast.dismiss(tLoad);
      const tPoll = toast.loading('Generating BIM Compliance & Advisory Report…', {
        description: 'AHA is preparing deterministic compliance remediation and source-cited BEP advisory guidance. This typically takes 15-90 seconds.',
      });
      const POLL_INTERVAL_MS = 2000;
      const MAX_POLLS = 90; // 90 * 2 s = 3 min
      let finalStatus = null;
      let pollErr = '';
      for (let i = 0; i < MAX_POLLS; i += 1) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const sResp = await apiFetch(`${API_URL}/api/ai-jobs/${job_id}`);
        if (!sResp.ok) {
          // Transient — keep polling unless we hit 404 (job vanished).
          if (sResp.status === 404) {
            throw new Error('Job vanished from the server — please retry.');
          }
          continue;
        }
        const sJson = await sResp.json();
        if (sJson.status === 'complete') { finalStatus = 'complete'; break; }
        if (sJson.status === 'failed')   { finalStatus = 'failed'; pollErr = sJson.error || ''; break; }
        // pending/running — keep waiting.
      }
      toast.dismiss(tPoll);

      if (finalStatus !== 'complete') {
        throw new Error(
          finalStatus === 'failed'
            ? `Report generation failed: ${pollErr || 'unknown error'}`
            : 'Report did not finish within 3 minutes. The job is still running on the server — try again in a moment or check Audit History.',
        );
      }

      // 3) Download the cached PDF via the generic ai-jobs handler.
      // This is fast (<1 s for a 60 KB PDF) so the existing
      // _downloadBlob helper handles the user-side save flow cleanly.
      await _downloadBlob(
        `${API_URL}/api/ai-jobs/${job_id}/download`,
        complianceReportDownloadName(audit?.ifc_file_name, auditId),
        'Remediation report failed',
        'application/pdf',
      );
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error('Remediation report failed', {
        description: e.message || 'Unknown error',
        duration: 10000,
      });
      setError(e.message || 'Remediation report failed');
    } finally {
      setRemediationLoading(false);
    }
  };

  /**
   * Generate a SINGLE-CATEGORY remediation report PDF.
   *
   * Same async flow as downloadRemediationReport — POST /start →
   * poll /api/ai-jobs/{job_id} → GET /download — but scoped to ONE
   * validation category so the resulting PDF can be shared with the
   * matching subcontractor without leaking the rest of the audit.
   */
  const downloadCategoryReport = async (category) => {
    if (!auditId || !category) return;
    setCategoryReportLoadingFor(category);
    const tLoad = toast.loading(`Starting ${category} category report…`);
    try {
      const startResp = await apiFetch(
        `${API_URL}/api/audits/${auditId}/category-report/start?category=${encodeURIComponent(category)}`,
        { method: 'POST' },
      );
      if (!startResp.ok) {
        const body = await startResp.text();
        throw new Error(`Start failed (${startResp.status}): ${body.slice(0, 200)}`);
      }
      const { job_id } = await startResp.json();
      toast.dismiss(tLoad);
      const tPoll = toast.loading(`Generating ${category} PDF…`, {
        description: 'Discipline-scoped report — should land in under a minute.',
      });
      let finalStatus = null;
      let pollErr = '';
      for (let i = 0; i < 90; i += 1) {
        await new Promise((r) => setTimeout(r, 2000));
        const sResp = await apiFetch(`${API_URL}/api/ai-jobs/${job_id}`);
        if (!sResp.ok) {
          if (sResp.status === 404) throw new Error('Job vanished from the server — please retry.');
          continue;
        }
        const sJson = await sResp.json();
        if (sJson.status === 'complete') { finalStatus = 'complete'; break; }
        if (sJson.status === 'failed')   { finalStatus = 'failed'; pollErr = sJson.error || ''; break; }
      }
      toast.dismiss(tPoll);
      if (finalStatus !== 'complete') {
        throw new Error(
          finalStatus === 'failed'
            ? `Generation failed: ${pollErr || 'unknown error'}`
            : 'Report did not finish within 3 minutes — try again.',
        );
      }
      await _downloadBlob(
        `${API_URL}/api/ai-jobs/${job_id}/download`,
        `AHA_${category}_${auditId}.pdf`,
        `${category} report failed`,
        'application/pdf',
      );
    } catch (e) {
      toast.dismiss(tLoad);
      toast.error(`${category} report failed`, {
        description: e.message || 'Unknown error',
        duration: 10000,
      });
    } finally {
      setCategoryReportLoadingFor(null);
    }
  };

  // Sprint 5 — export the failure clusters as a BCF 2.1 archive so the
  // BIM coordinator can pull AHA's findings into Revit / Solibri /
  // BIMcollab as native issues with affected GlobalIds attached.
  const downloadBcfArchive = async () => {
    if (!auditId) return;
    setBcfLoading(true);
    try {
      await _downloadBlob(
        `${API_URL}/api/audits/${auditId}/bcf/export`,
        `AHA_${auditId}.bcfzip`,
        'BCF export failed',
        'application/zip',
      );
    } catch (e) {
      setError(e.message || 'BCF export failed');
    } finally {
      setBcfLoading(false);
    }
  };

  // AIM-register page size PER REQUEST — not a total cap. The fetch
  // loop below keeps requesting pages until the backend reports
  // ``has_more: false``, so any audit size (10, 687, 5k, 60k+ elements)
  // is fully loaded before dashboards/exports use it.
  const PAGE_SIZE = 1000;

  const fetchResults = useCallback(async () => {
    const mapRow = (r) => ({
      elementId: r.element_id,
      assetName: r.asset_name || r.element_id,
      ifcClass: r.ifc_class || '',
      objectType: r.object_type || '',
      storey: r.storey || '',
      confidenceScore: Math.round(r.confidence_score || 0),
      status: r.certification_status === 'SELF_CERTIFIED' ? 'SELF_CERTIFIED'
            : r.overall_status === 'FAIL' ? 'FAILED'
            : 'HITL_FLAGGED',
      overallStatus: r.overall_status || 'REVIEW',
      totalChecks: r.total_checks || 0,
      passed: r.passed || 0,
      warnings: r.warnings || 0,
      failures: r.failures || 0,
      issues: r.issues || [],
    });

    // One register page, with retries — Supabase occasionally throws
    // transient statement-timeout/disconnect 500s under parallel load;
    // a single flaky page must never abort the whole stream.
    const fetchPage = async (offset, attempt = 0) => {
      const resp = await apiFetch(
        `${API_URL}/api/audits/${auditId}/results?limit=${PAGE_SIZE}&offset=${offset}`
      ).catch((e) => { if (attempt < 2) return null; throw e; });
      if (!resp || !resp.ok) {
        if (attempt < 2) {
          await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
          return fetchPage(offset, attempt + 1);
        }
        throw new Error(`Failed to fetch results page at offset ${offset}`);
      }
      return resp.json();
    };

    try {
      // Kick off the pre-computed aggregates fetch immediately (not
      // awaited) — the dashboard charts render from this tiny payload
      // long before the row stream finishes. On failure the charts
      // simply fall back to the streamed rows.
      apiFetch(`${API_URL}/api/audits/${auditId}/results/aggregates`)
        .then((r) => (r.ok ? r.json() : null))
        .then((agg) => { if (agg) setAggregates(agg); })
        .catch(() => {});

      const firstResp = await fetchPage(0);
      if (!firstResp) throw new Error('Failed to fetch results');
      const first = firstResp;
      // If the audit is still running, bounce the user to the live
      // progress page rather than rendering a half-empty dashboard
      // (the old behaviour showed misleading "100% validated" labels
      // sourced from parsed_elements while no actual validation rows
      // had landed yet).
      const liveStatus = first?.audit?.status;
      if (liveStatus === 'processing' || liveStatus === 'queued' || liveStatus === 'pending') {
        navigate(`/audits/${auditId}/progress`, { replace: true });
        return;
      }

      setAudit(first.audit);
      let all = (first.results || []).map(mapRow);
      const total = first?.pagination?.total ?? all.length;
      setTotalRecords(total);
      setData(all);
      setLoading(false); // render the first page immediately

      // Stream the remaining pages until every row is in. Pages are
      // fetched with BOUNDED parallelism (4 in flight) — sequential
      // streaming of a 60k-row register through the ingress takes
      // minutes, while the offsets are all known from the real
      // ``pagination.total``. Results are assembled in offset order so
      // row order stays stable. Charts refine as batches land; exports
      // unlock only when the register is complete.
      const hasMore = first?.pagination?.has_more ?? (all.length < total);
      if (hasMore) setFetchingAll(true);
      const CONCURRENCY = 4;
      const offsets = [];
      for (let o = all.length; o < total; o += PAGE_SIZE) offsets.push(o);
      for (let i = 0; i < offsets.length; i += CONCURRENCY) {
        const batch = offsets.slice(i, i + CONCURRENCY);
        const pages = await Promise.all(batch.map(async (o) => {
          const json = await fetchPage(o);
          return (json.results || []).map(mapRow);
        }));
        all = all.concat(...pages);
        setData(all);
      }

      // Defensive tail-walk: if any page came back short, keep paging
      // via ``has_more`` so we never silently settle for a partial
      // register. No-ops on the normal path.
      let offset = all.length;
      while (offset < total) {
        const json = await fetchPage(offset);
        const rows = (json.results || []).map(mapRow);
        if (rows.length === 0) break; // defensive: never loop forever
        all = all.concat(rows);
        offset += rows.length;
        setData(all);
        if (!(json?.pagination?.has_more ?? false)) break;
      }
    } catch (err) {
      // F8 — production-integrity. NEVER fall back to mock/sample data on
      // fetch failure. If nothing loaded, show an empty result set + a
      // clear error. If a later page failed we keep the partial rows for
      // context, but the export gate stays locked because
      // data.length < totalRecords.
      setError(err.message || 'Audit results could not be loaded.');
    } finally {
      setLoading(false);
      setFetchingAll(false);
    }
  }, [auditId, navigate]);

  useEffect(() => {
    if (!auditId) {
      // No audit ID in the URL → empty state, no mock backfill.
      setData([]);
      setAudit(null);
      setError('No audit selected.');
      setLoading(false);
      return;
    }
    fetchResults();
  }, [auditId, fetchResults]);

  // ── Category filter — encapsulated in useCategoryFilter ────────────
  // The hook owns the lazy fetch, cache, and derived chart memos. See
  // /lib/useCategoryFilter.js for the full surface. We destructure
  // every legacy variable name the rest of the render path uses so
  // this refactor is a pure rename, not a behavioural change.
  const {
    selectedCategory,
    selectedCategoryLabel,
    setSelectedCategory,
    clearSelection: clearCategorySelection,
    detailsLoading,
    detailsError,
    detailsTruncated,
    filteredView,
    chartData,
    chartSummary,
  } = useCategoryFilter({ auditId, audit, baseData: data });

  // ``summary`` is still referenced in a handful of legacy code paths
  // (cluster headlines, KPI tiles). Keep it in scope to avoid a TDZ
  // ReferenceError mid-render.
  const summary = useMemo(() => audit?.summary || {}, [audit]);

  // Batch B — confidence/certification only reflect QG baseline checks
  // (naming/schema/fm/geometry/spatial), never custom_bep_rules. When
  // none of those are enabled in the audit's profile, the confidence
  // column + avg-confidence line + Self Certified/HITL Flagged tiles
  // are meaningless and hidden entirely.
  const hasQgCategory = !!(
    audit?.validation_profile &&
    QG_CATEGORIES.some((k) => audit.validation_profile[k])
  );


  const columns = useMemo(() => [
    {
      accessorKey: 'elementId',
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')} className="-ml-4 h-8 font-semibold">
          Element ID <ArrowUpDown className="ml-2 h-3 w-3" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-mono text-xs">{row.getValue('elementId')}</div>,
    },
    {
      accessorKey: 'assetName',
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')} className="-ml-4 h-8 font-semibold">
          Asset Name <ArrowUpDown className="ml-2 h-3 w-3" />
        </Button>
      ),
      cell: ({ row }) => <div className="font-medium text-sm">{row.getValue('assetName')}</div>,
    },
    {
      accessorKey: 'ifcClass',
      header: 'IFC Class',
      cell: ({ row }) => <Badge variant="outline" className="text-xs">{row.getValue('ifcClass')}</Badge>,
    },
    {
      accessorKey: 'storey',
      header: 'Storey',
      cell: ({ row }) => <div className="text-sm text-muted-foreground">{row.getValue('storey') || '-'}</div>,
    },
    {
      accessorKey: 'confidenceScore',
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')} className="-ml-4 h-8 font-semibold"
                title="Baseline-check confidence — BEP rule results do not affect it.">
          Confidence <ArrowUpDown className="ml-2 h-3 w-3" />
        </Button>
      ),
      cell: ({ row }) => {
        const score = row.getValue('confidenceScore');
        const color = score >= 75 ? 'text-[hsl(var(--success))]' : score >= 50 ? 'text-[hsl(var(--warning))]' : 'text-[hsl(var(--destructive))]';
        return <div className={`font-semibold ${color}`}>{score}%</div>;
      },
    },
    {
      accessorKey: 'overallStatus',
      header: ({ column }) => (
        <Button variant="ghost" onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')} className="-ml-4 h-8 font-semibold">
          Status <ArrowUpDown className="ml-2 h-3 w-3" />
        </Button>
      ),
      cell: ({ row }) => {
        const status = row.getValue('overallStatus');
        const styles = {
          'PASS': 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
          'WARN': 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
          'REVIEW': 'bg-accent/15 text-accent border-accent/30',
          'FAIL': 'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
        };
        return <Badge className={`${styles[status] || styles.REVIEW} border text-xs`}>{status}</Badge>;
      },
    },
    {
      accessorKey: 'status',
      header: 'Certification',
      cell: ({ row }) => {
        const cert = row.getValue('status');
        const isOk = cert === 'SELF_CERTIFIED';
        return (
          <Badge className={isOk
            ? 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))] border border-[hsl(var(--success))]/30'
            : 'bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] border border-[hsl(var(--warning))]/30'
          }>
            {cert.replace('_', ' ')}
          </Badge>
        );
      },
    },
  ], []);

  const visibleColumns = useMemo(
    () => hasQgCategory ? columns : columns.filter((c) => !['confidenceScore', 'status'].includes(c.accessorKey)),
    [columns, hasQgCategory],
  );

  const table = useReactTable({
    data, columns: visibleColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    // UI-side pagination keeps the DOM small on 10k+ element audits.
    // The FULL dataset stays in ``data`` for charts + exports.
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 100 } },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: { sorting, globalFilter },
  });

  // F8 — single source of truth for whether the user is allowed to export.
  // Both the UI (button disabled) AND exportToExcel() check this; any
  // mismatch would silently leak fake/partial reports.
  const exportGate = useMemo(() => {
    if (!audit) {
      return { allowed: false, reason: 'Audit results could not be loaded.' };
    }
    if (audit.status !== 'completed') {
      return {
        allowed: false,
        reason: `Export disabled because this audit is ${audit.status || 'incomplete'}.`,
      };
    }
    if (audit.validation_mode === 'blocked' || audit.export_blocked) {
      return {
        allowed: false,
        reason:
          audit.export_block_reason ||
          audit.failure_reason ||
          'Export disabled because this audit is incomplete or blocked.',
      };
    }
    const coverage = Number(audit.validation_coverage_percent || 0);
    if (coverage < 100) {
      return {
        allowed: false,
        reason: `Export disabled — coverage is ${coverage.toFixed(2)}%, not a full validation.`,
      };
    }
    if (!Array.isArray(data) || data.length === 0) {
      return { allowed: false, reason: 'No real audit results found.' };
    }
    // Never export a partial register. ``totalRecords`` is the real DB
    // count from the backend; exports unlock only once every page has
    // streamed in.
    if (fetchingAll || (totalRecords > 0 && data.length < totalRecords)) {
      return {
        allowed: false,
        reason: `Full dataset still loading (${data.length.toLocaleString()} of ${totalRecords.toLocaleString()} rows) — export unlocks when complete.`,
      };
    }
    return { allowed: true, reason: '' };
  }, [audit, data, fetchingAll, totalRecords]);

  const exportToExcel = () => {
    try {
      // Re-check the gate at call time. The button is disabled, but defense-
      // in-depth keeps a stale state or accessibility-bypass click from
      // generating a report off blocked / sampled / failed data.
      if (!exportGate.allowed) {
        setError(exportGate.reason);
        return;
      }

      // Flatten the data into Excel-friendly rows. Arrays → comma strings.
      // Use proper labels instead of camelCase keys so the spreadsheet is readable.
      const rows = data.map((r) => ({
        'Element ID': r.elementId || '',
        'Asset Name': r.assetName || '',
        'IFC Class': r.ifcClass || '',
        'Object Type': r.objectType || '',
        'Storey': r.storey || '',
        'Confidence (%)': typeof r.confidenceScore === 'number' ? r.confidenceScore : '',
        'Overall Status': r.overallStatus || '',
        'Certification': (r.status || '').replace(/_/g, ' '),
        'Total Checks': r.totalChecks || 0,
        'Passed': r.passed || 0,
        'Warnings': r.warnings || 0,
        'Failures': r.failures || 0,
        'Issues': Array.isArray(r.issues) ? r.issues.join(' | ') : (r.issues || ''),
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);

      // Auto-fit column widths (approximate by longest cell value per column)
      const colKeys = Object.keys(rows[0]);
      worksheet['!cols'] = colKeys.map((k) => {
        const maxLen = Math.max(
          k.length,
          ...rows.map((r) => String(r[k] ?? '').length)
        );
        return { wch: Math.min(Math.max(maxLen + 2, 10), 50) };
      });

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'AIM Register');

      // Build filename with real project/audit context. F8 — NEVER a placeholder.
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const fileLabel =
        audit?.ifc_file_name?.replace(/\.[^.]+$/, '') ||
        (auditId ? `audit_${auditId.slice(0, 8)}` : `audit_${ts}`);
      const filename = `AIM_Register_${fileLabel}_${ts}.xlsx`;

      // Use blob + anchor download. Works in all browsers including Safari.
      // (XLSX.writeFile uses an internal saveAs that's flaky in some Safari/iOS builds.)
      const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke after a tick so download can latch onto the URL
      setTimeout(() => URL.revokeObjectURL(url), 100);

      setError('');
    } catch (err) {
      console.error('Export to Excel failed:', err);
      setError(`Export failed: ${err.message || 'Unknown error'}. Try refreshing the page.`);
    }
  };

  const elementSummary = summaryMetric(summary.element_compliance);
  const elementRuleSummary = applicableElementChecks(summary);
  const elementStatusResolution = useMemo(
    () => resolveElementStatusCounts(summary, audit?.total_elements),
    [summary, audit?.total_elements]
  );
  const statusCounts = useMemo(() => ({
    PASS: elementStatusResolution.counts?.PASS ?? null,
    WARN: elementStatusResolution.counts?.WARN ?? null,
    REVIEW: elementStatusResolution.counts?.REVIEW ?? null,
    FAIL: elementStatusResolution.counts?.FAIL ?? null,
    NOT_ASSESSED: elementStatusResolution.counts?.NOT_ASSESSED ?? null,
    SELF_CERTIFIED: isFiniteNumber(summary.self_certified) ? summary.self_certified : null,
    HITL_FLAGGED: isFiniteNumber(summary.hitl_flagged) ? summary.hitl_flagged : null,
  }), [summary, elementStatusResolution]);
  const processingCoverage = isFiniteNumber(audit?.parsed_elements) && isFiniteNumber(audit?.total_elements) && audit.total_elements > 0
    ? (audit.parsed_elements / audit.total_elements) * 100
    : null;
  const activeRuleEvidence = useMemo(
    () => resolveActiveRuleEvidence(summary, audit?.active_rule_count),
    [summary, audit?.active_rule_count]
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="results-loading">
          <Loader2 className="h-8 w-8 animate-spin text-accent" />
          <span className="ml-3 text-muted-foreground">Loading results...</span>
        </div>

      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-6 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              {auditId && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate(audit?.project_id ? `/projects/${audit.project_id}` : '/history')}
                  data-testid="back-btn"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back
                </Button>
              )}
              <h1 className="text-3xl font-bold text-foreground" data-testid="results-title">
                AIM Register
              </h1>
            </div>
            {audit && (
              <p className="text-sm text-muted-foreground flex items-center gap-2 flex-wrap" data-testid="audit-summary-line">
                <span>{audit.ifc_file_name}</span>
                {audit.naming_mismatch_ack && (
                  <span title={audit.ifc_file_name}
                    className="inline-block text-[10px] px-1.5 py-0.5 rounded border border-amber-500/50 bg-amber-500/10 text-amber-700"
                    data-testid="naming-mismatch-badge-results">
                    Naming mismatch — validated on user confirmation
                  </span>
                )}
                <SchemaBadge schemaVersion={audit.schema_version} />
                {/* Mar 2, 2026 — Authoring-tool chip beside the schema
                    chip. Reads from ``audit.summary.authoring_tool``
                    persisted by the parser at audit time; new audits
                    populate it automatically, historical audits show
                    the fallback "unknown" state until the user clicks
                    the IFC Composition panel below (which lazy-loads
                    via the on-demand endpoint and back-fills the row). */}
                <AuthoringToolBadge tool={audit.summary?.authoring_tool} />
                <ModelBadge />
                <span>&middot; {audit.total_elements?.toLocaleString()} total elements &middot; {new Date(audit.created_at).toLocaleString()}</span>
              </p>
            )}
            {audit && isFiniteNumber(audit.total_elements) && audit.total_elements > 0 && (
              <p
                className="text-xs mt-1 text-muted-foreground"
                data-testid="validation-coverage-line"
                title="IFC processing coverage is parsed elements divided by total IFC elements. It is separate from assessment coverage."
              >
                {processingCoverage == null ? (
                  <span data-testid="validation-coverage-unavailable">IFC processing coverage unavailable — parsed element count was not recorded.</span>
                ) : (
                  <>
                    Validated <strong className="text-foreground">{audit.parsed_elements.toLocaleString()}</strong>{' '}
                    of <strong className="text-foreground">{audit.total_elements.toLocaleString()}</strong>{' '}
                    IFC elements &middot; IFC processing coverage:{' '}
                    <strong
                      className={processingCoverage >= 100 ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--destructive))]'}
                      data-testid="validation-coverage-percent"
                    >
                      {formatPercent(processingCoverage)}
                    </strong>
                  </>
                )}
                {audit.validation_mode && audit.validation_mode !== 'full' && (
                  <>
                    {' '}&middot; Mode:{' '}
                    <span className="uppercase font-semibold" data-testid="validation-mode-badge">
                      {audit.validation_mode}
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {isReadOnly && (
          <Alert className="mb-6 border-amber-500/40 bg-amber-500/5" data-testid="results-read-only-banner">
            <LockKeyhole className="h-4 w-4 text-amber-700 dark:text-amber-300" />
            <AlertDescription>
              <strong>Read-only demo.</strong> Browse and download stored results; generating new AI reports is owner-only.
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert
            className="mb-4 border-[hsl(var(--destructive))]"
            data-testid="results-error-alert"
          >
            <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
            <AlertDescription className="text-[hsl(var(--destructive))]">
              {error}
            </AlertDescription>
          </Alert>
        )}

        {/* F12 — when the audit was REFUSED (validation_mode='blocked'),
            show the strong "no approved rules" message above everything else. */}
        {audit && audit.validation_mode === 'blocked' && (
          <Alert
            className="mb-4 border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/10"
            data-testid="validation-blocked-banner"
          >
            <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
            <AlertDescription className="text-[hsl(var(--destructive))]">
              <strong>Validation blocked: no approved BEP/rules found.</strong>
              <br />
              {audit.failure_reason || audit.export_block_reason ||
                'No confirmed rules found for this project. Validation cannot start. Please upload and approve a BEP first.'}
            </AlertDescription>
          </Alert>
        )}

        {/* F5 — Partial-validation warning. Shows whenever coverage < 100%
            so users cannot mistake a sample/blocked run for a full audit. */}
        {audit && audit.total_elements > 0 &&
         audit.validation_mode !== 'blocked' &&
         processingCoverage !== null && processingCoverage < 100 && (
          <Alert
            className="mb-4 border-[hsl(var(--destructive))] bg-[hsl(var(--destructive))]/5"
            data-testid="partial-validation-warning"
          >
            <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
            <AlertDescription className="text-[hsl(var(--destructive))]">
              <strong>This is not a full IFC validation. Do not use this as a final compliance report.</strong>
              <br />
              {audit.export_block_reason ||
                `Only ${audit.parsed_elements.toLocaleString()} of ${audit.total_elements.toLocaleString()} elements were processed (${formatPercent(processingCoverage)}).`}
            </AlertDescription>
          </Alert>
        )}

        {/* Stats Cards — element-level rollup (one number per real IFC element).
            Worst-rule outcome wins: an element is PASS only if every rule it
            was checked against passed. The subtitle ("1 of 19 elements")
            makes the unit explicit so reviewers don't conflate these with the
            rule-level counts below. */}
        <div className="mb-2 flex items-center gap-2">
          <p className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold" data-testid="element-rollup-heading">
            Per element ({isFiniteNumber(audit?.total_elements) ? `${audit.total_elements.toLocaleString()} total` : 'total unavailable'})
          </p>
          <span className="text-[10px] text-muted-foreground/70" title="One number per IFC element. An element is PASS only if every rule check on it passed; the worst outcome wins.">
            ⓘ
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
          {[
            { label: 'Pass', count: statusCounts.PASS, color: 'success' },
            { label: 'Warn', count: statusCounts.WARN, color: 'warning' },
            { label: 'Review', count: statusCounts.REVIEW, color: 'accent' },
            { label: 'Fail', count: statusCounts.FAIL, color: 'destructive' },
          ].map(({ label, count, color }) => {
            const tot = isFiniteNumber(audit?.total_elements) ? audit.total_elements : null;
            const pct = isFiniteNumber(count) && tot !== null && tot > 0 ? (count / tot) * 100 : null;
            return (
              <div key={label} className="card-elevated p-4" data-testid={`stat-${label.toLowerCase()}`}
                   title="% of all elements. FAIL = 2 or more failed checks; REVIEW = exactly 1.">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">{label}</p>
                <p className={`text-2xl font-bold ${color === 'accent' ? 'text-accent' : `text-[hsl(var(--${color}))]`}`}>
                  {count ?? 'Unavailable'}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5" data-testid={`stat-${label.toLowerCase()}-sub`}>
                  {count == null || tot === null ? 'Assessed-element aggregate or denominator unavailable' : `${count} of ${tot} element${tot === 1 ? '' : 's'} (${formatPercent(pct)})`}
                </p>
              </div>
            );
          })}
          {/* Not Assessed — excluded from the pass-rate denominator, so it
              gets its own plain-language reason instead of a "% of total"
              subline (which would misleadingly imply it competes with
              pass/warn/review/fail for the same 100%). */}
          <div className="card-elevated p-4" data-testid="stat-not_assessed">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Not Assessed</p>
            <p className="text-2xl font-bold text-muted-foreground">
              {statusCounts.NOT_ASSESSED ?? 'Unavailable'}
            </p>
            <p className="text-[10px] text-muted-foreground mt-0.5" data-testid="stat-not_assessed-sub">
              {statusCounts.NOT_ASSESSED == null
                ? 'Assessed-element aggregate unavailable'
                : `${statusCounts.NOT_ASSESSED} element${statusCounts.NOT_ASSESSED === 1 ? '' : 's'} — no confirmed rule applies yet`}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-6">
          {hasQgCategory && (
            <>
              <div className="card-elevated p-4" data-testid="stat-certified"
                   title="Baseline-check confidence — BEP rule results do not affect it.">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Self Certified</p>
                <p className="text-2xl font-bold text-[hsl(var(--success))]">{statusCounts.SELF_CERTIFIED ?? 'Unavailable'}</p>
              </div>
              <div className="card-elevated p-4" data-testid="stat-flagged"
                   title="Baseline-check confidence — BEP rule results do not affect it.">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">HITL Flagged</p>
                <p className="text-2xl font-bold text-[hsl(var(--warning))]">{statusCounts.HITL_FLAGGED ?? 'Unavailable'}</p>
              </div>
            </>
          )}
        </div>

        {hasQgCategory && summary.avg_confidence != null && (
          <Alert className="mb-6 border-accent">
            <AlertCircle className="h-4 w-4 text-accent" />
            <AlertDescription>
              Average confidence: <strong>{summary.avg_confidence}%</strong>{' '}
              <span className="text-muted-foreground text-xs" title="Baseline-check confidence — BEP rule results do not affect it.">
                (baseline checks only)
              </span>{' '}
              Items below 75% confidence are flagged for Human-In-The-Loop review.
            </AlertDescription>
            {summary.element_compliance && (
              <AlertDescription className="mt-2 text-xs" data-testid="element-compliance-secondary">
                Assessed-element compliance: {elementSummary.state || (isFiniteNumber(elementSummary.pass)
                  ? `${elementSummary.pass}/${elementSummary.assessed} passed (${formatPercent(elementSummary.rate)})`
                  : 'Unavailable')}.
              </AlertDescription>
            )}
          </Alert>
        )}

        {/* Full-dataset streaming indicator — charts below refine as
            pages arrive; exports stay locked until every row is in. */}
        {fetchingAll && (
          <Alert className="mb-4 border-accent" data-testid="register-loading-banner">
            <Loader2 className="h-4 w-4 animate-spin text-accent" />
            <AlertDescription>
              Loading the full AIM register — {data.length.toLocaleString()} of {totalRecords.toLocaleString()} records so far.
              Charts update as data streams in; exports unlock when complete.
            </AlertDescription>
          </Alert>
        )}

        {/* Validation KPI Dashboard — Feb 28, 2026.
            Headline summary mounted above the existing Governance Metrics
            block and the CategoryBreakdown. Reads `audit` + the audit's
            already-fetched `category_breakdown` (no extra request). */}
        <ValidationKpiDashboard
          audit={audit}
        />

        {/* Sprint 1 — Governance Metrics (Applicability Engine + Storage) */}
        {audit && (audit.active_rule_count > 0 || audit.applicable_checks_count > 0 || audit.audit_duration_seconds > 0) && (
          <div className="card-elevated p-4 mb-6" data-testid="governance-metrics">
            <h3 className="text-sm font-semibold text-foreground mb-3">Governance Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Array.isArray(audit.summary?.model_level_checks) && audit.summary.model_level_checks.length > 0 && (
                <div className="p-3 rounded-md border border-border bg-card" data-testid="metric-model-checks">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Model-level Checks</p>
                  <p className="text-xl font-semibold text-foreground">
                    {audit.summary.model_level_checks.length}
                  </p>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Once per file · {audit.summary.model_level_checks.filter(c => c.status === 'PASS').length} passed
                  </p>
                  {audit.summary.model_preflight_compliance?.assessed > 0 && <p className="text-xs mt-1" data-testid="model-preflight-compliance">
                    Preflight: {audit.summary.model_preflight_compliance.passed}/{audit.summary.model_preflight_compliance.assessed} passed ({audit.summary.model_preflight_compliance.pass_rate}%)
                  </p>}
                </div>
              )}
              <div className="p-3 rounded-md border border-border bg-card" data-testid="metric-active-rule-snapshot"
                   title={activeRuleEvidence.immutableSnapshot
                     ? 'Rules captured in the immutable active confirmed-rule snapshot for this audit.'
                     : 'Historical active-rule count; immutable rule identifiers were not stored by this audit schema.'}>
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  {activeRuleEvidence.immutableSnapshot ? 'Active rule snapshot' : 'Active rules at audit'}
                </p>
                <p className="text-xl font-semibold text-foreground">{activeRuleEvidence.available ? activeRuleEvidence.count : 'Unavailable'}</p>
                <p className="text-[10px] text-muted-foreground mt-1">
                  {activeRuleEvidence.immutableSnapshot
                    ? 'Confirmed rules captured at audit start'
                    : activeRuleEvidence.available ? 'Historical count · immutable rule IDs unavailable' : 'Active-rule evidence unavailable'}
                </p>
              </div>
              <div className="p-3 rounded-md border border-border bg-card" data-testid="metric-applicable-checks">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Applicable element checks</p>
                <p className="text-xl font-semibold text-foreground">{elementRuleSummary.available ? elementRuleSummary.count.toLocaleString() : 'Unavailable'}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Custom BEP element-rule checks; separate from model/file checks</p>
              </div>
              <div className="p-3 rounded-md border border-border bg-card" data-testid="metric-duration">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground">Audit Duration</p>
                <p className="text-xl font-semibold text-foreground">{audit.audit_duration_seconds ? `${audit.audit_duration_seconds}s` : '—'}</p>
                <p className="text-[10px] text-muted-foreground mt-1">Pipeline wall-clock time</p>
              </div>
            </div>
          </div>
        )}

        {/* P02 Batch 5 — "Rules not run" transparency panel. Groups
            summary.skipped_by_applicability by skip_reason; renders
            nothing when the audit has no skips. */}
        <RulesNotRunPanel summary={audit?.summary} />

        {/* Category breakdown — extracted to <CategoryBreakdown> on
            Feb 26, 2026. The component owns the per-row toggle UX,
            the per-category PDF export button, and the friendly hint
            text. State lives in the parent + flows through props. */}
        <CategoryBreakdown
          audit={audit}
          selectedCategory={selectedCategory}
          onSelectCategory={setSelectedCategory}
          onExportCategoryReport={isReadOnly ? null : downloadCategoryReport}
          categoryReportLoadingFor={categoryReportLoadingFor}
        />

        {/* Property coverage — descriptive model-wide aggregate,
            no pass/fail. Renders only when the audit stored one. */}
        <PropertyCoveragePanel auditId={auditId} />

        {/* Active-filter status banner — extracted to
            <CategoryFilterBanner>. Sits directly above the charts so
            the user always knows whether they're looking at the full
            dataset or a category-scoped view. */}
        <CategoryFilterBanner
          selectedCategory={selectedCategory}
          selectedCategoryLabel={selectedCategoryLabel}
          detailsLoading={detailsLoading}
          detailsError={detailsError}
          detailsTruncated={detailsTruncated}
          chartData={chartData}
          onClear={clearCategorySelection}
        />

        {/* Visual Dashboard Charts */}
        {selectedCategory && detailsLoading ? (
          <div
            className="card-elevated p-8 mb-6 flex flex-col items-center justify-center min-h-[280px]"
            data-testid="category-loading-skeleton"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--accent))] mb-3" />
            <p className="text-sm text-muted-foreground">
              Computing <strong>{selectedCategoryLabel}</strong> breakdown…
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              Pulling just this category's rule rows — usually under a second.
            </p>
          </div>
        ) : selectedCategory && chartData.length === 0 && !filteredView ? (
          // Pre-cache hit before details arrive — show same skeleton.
          <div
            className="card-elevated p-8 mb-6 flex flex-col items-center justify-center min-h-[280px]"
            data-testid="category-loading-skeleton"
          >
            <Loader2 className="h-8 w-8 animate-spin text-[hsl(var(--accent))] mb-3" />
            <p className="text-sm text-muted-foreground">
              Loading <strong>{selectedCategoryLabel}</strong>…
            </p>
          </div>
        ) : selectedCategory && chartData.length === 0 ? (
          <div
            className="card-elevated p-6 mb-6 text-center text-sm text-muted-foreground"
            data-testid="category-empty-state"
          >
            No elements were evaluated against the <strong>{selectedCategoryLabel}</strong> category in this audit.
          </div>
        ) : (
          <>
            {/* Mar 2, 2026 — IFC Composition & Property Reality panel
                sits ABOVE the failures-by-class breakdown so reviewers
                can frame the failure numbers against the element
                population and applicable scope BEFORE drawing
                conclusions about data quality. */}
            <div className="mb-4">
              <IFCCompositionPanel audit={audit} auditId={auditId} />
            </div>
            <IFCValidationPanel
              data={chartData}
              summary={chartSummary}
              auditId={auditId}
              // Aggregates cover the WHOLE audit — suppress them while a
              // category drill-down is active so the charts reflect the
              // filtered per-category view instead.
              aggregates={selectedCategory ? null : aggregates}
              profile={audit?.validation_profile}
            />
          </>
        )}

        {/* Controls */}
        <div className="card-elevated p-4 mb-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search elements..."
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-9"
                data-testid="results-search"
              />
            </div>
            <Button
              onClick={exportToExcel}
              variant="outline"
              disabled={!exportGate.allowed}
              title={!exportGate.allowed ? exportGate.reason : ''}
              data-testid="export-btn"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
            {!isReadOnly && <Button
              onClick={downloadRemediationReport}
              variant="default"
              disabled={remediationLoading || audit?.status !== 'completed'}
              title={
                audit?.status !== 'completed'
                  ? 'Remediation report is only available for completed audits.'
                  : 'AI prepares deterministic remediation plus a source-cited BEP advisory review. Advisory only — humans arbitrate.'
              }
              data-testid="ai-remediation-report-btn"
              className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white"
            >
              {remediationLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {remediationLoading ? 'Generating…' : 'BIM Compliance & Advisory Report'}
            </Button>}
            <Button
              onClick={downloadBcfArchive}
              variant="outline"
              disabled={bcfLoading || (audit?.status !== 'completed' && audit?.status !== 'completed_with_warnings')}
              title={
                (audit?.status !== 'completed' && audit?.status !== 'completed_with_warnings')
                  ? 'BCF export is only available for completed audits.'
                  : 'Download failures as a BCF 2.1 archive (.bcfzip). Open in Revit, Solibri, ArchiCAD, BIMcollab, or any IDS-aware BIM tool to review affected GlobalIds as native issues.'
              }
              data-testid="export-bcf-btn"
            >
              {bcfLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileArchive className="mr-2 h-4 w-4" />
              )}
              {bcfLoading ? 'Building…' : 'Export BCF'}
            </Button>
            <Button onClick={() => setShowEmailPrompt(!showEmailPrompt)} variant="outline" data-testid="email-report-btn">
              <Mail className="mr-2 h-4 w-4" />
              Email Report
            </Button>
          </div>
          {showEmailPrompt && (
            <div className="mt-3 flex items-center gap-2">
              <Input
                placeholder="recipient@email.com"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                className="max-w-xs"
                data-testid="email-input"
              />
              <Button
                size="sm"
                onClick={() => {
                  exportToExcel();
                  const subject = encodeURIComponent(`AHA Audit Report - ${audit?.ifc_file_name || 'Audit'}`);
                  const body = encodeURIComponent(
                    `Hello,\n\nPlease find attached the AIM Register audit report.\n\n` +
                    `File: ${audit?.ifc_file_name || 'N/A'}\n` +
                    `Date: ${audit?.created_at ? new Date(audit.created_at).toLocaleDateString() : 'N/A'}\n` +
                    `Total Elements: ${audit?.total_elements || 0}\n` +
                    `Pass Rate: ${summary?.pass_rate || 0}%\n` +
                    `Avg Confidence: ${summary?.avg_confidence || 0}%\n` +
                    `Self Certified: ${statusCounts.SELF_CERTIFIED}\n` +
                    `HITL Flagged: ${statusCounts.HITL_FLAGGED}\n\n` +
                    `The Excel report has been downloaded. Please attach it to this email.\n\n` +
                    `Regards,\nAHA - Autonomous Handover Agent`
                  );
                  window.open(`mailto:${emailTo}?subject=${subject}&body=${body}`, '_blank');
                  setShowEmailPrompt(false);
                  setEmailTo('');
                }}
                disabled={!emailTo}
                data-testid="send-email-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1" /> Open Email Client
              </Button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="card-elevated overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((hg) => (
                  <TableRow key={hg.id}>
                    {hg.headers.map((h) => (
                      <TableHead key={h.id}>
                        {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow key={row.id} className="hover:bg-muted/50">
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center text-muted-foreground"
                      data-testid="results-empty-state"
                    >
                      {error
                        ? 'Audit results could not be loaded.'
                        : 'No real audit results found.'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
        <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-sm text-muted-foreground">
          <span data-testid="results-table-footer">
            {fetchingAll
              ? `Loading full register… ${data.length.toLocaleString()} of ${totalRecords.toLocaleString()} records loaded`
              : `Showing ${table.getFilteredRowModel().rows.length.toLocaleString()} of ${(totalRecords || data.length).toLocaleString()} records`}
          </span>
          {table.getPageCount() > 1 && (
            <div className="flex items-center gap-2" data-testid="results-table-pagination">
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                data-testid="results-table-prev-page"
              >
                Previous
              </Button>
              <span className="text-xs tabular-nums" data-testid="results-table-page-indicator">
                Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount().toLocaleString()}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                data-testid="results-table-next-page"
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
      <IdsValidationResultsModal
        open={idsValidationModalOpen}
        onOpenChange={setIdsValidationModalOpen}
        result={idsValidationResult}
      />
    </DashboardLayout>
  );
}
